import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as ArrowIcon } from '../../assets/icons/down-arrow.svg';
import { ReactComponent as RightArrowIcon } from '../../assets/icons/right-arrow.svg';
import { ReactComponent as SearchIcon } from '../../assets/icons/search-icon.svg';
import { ReactComponent as Search } from '../../assets/icons/search.svg';
import { ReactComponent as DownArrowIcon } from '../../assets/icons/up-arrow.svg';

export interface Option {
  label: string;
  value: string;
  labelNode?: React.ReactNode;
  [key: string]: any;
}

interface DrillDownGroup {
  key: string;
  label: string;
  items: Option[];
  totalCount: number;
  selectedCount: number;
  isAllSelected: boolean;
  isPartialSelected: boolean;
}

/**
 * Info Icon component for displaying tooltip trigger.
 * @returns {React.JSX.Element} SVG icon element.
 */
function InfoIcon(): React.JSX.Element {
  return (
    <svg width="14" height="14" viewBox="0 0 20 20" fill="currentColor" className="info-icon-svg">
      <path
        fillRule="evenodd"
        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
        clipRule="evenodd"
      />
    </svg>
  );
}

/**
 * Resolves the string value for a specific drill-down level key from an option.
 * @param {Option} option - The dropdown option item.
 * @param {string} levelKey - The drill-down property key name.
 * @returns {string} Resolved level value string.
 */
const getOptionLevelValue = (option: Option, levelKey: string): string => {
  const rawValue =
    option[levelKey] ??
    option.raw?.[levelKey] ??
    option.data?.[levelKey] ??
    option.item?.[levelKey];

  if (rawValue !== undefined && rawValue !== null && String(rawValue).trim() !== '') {
    return String(rawValue).trim();
  }
  return 'Unassigned';
};

/**
 * Formats a camelCase or snake_case property key into a human-readable title.
 * @param {string} keyName - The level property key name.
 * @returns {string} Formatted level title.
 */
const formatLevelTitle = (keyName: string): string => {
  return keyName
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/^./, (character) => character.toUpperCase());
};

/**
 * Formats the full hierarchical path string for an option across drill-down levels.
 * @param {Option} option - The dropdown option item.
 * @param {string[]} levels - The list of drill-down level keys.
 * @returns {string} Formatted hierarchical path string.
 */
const getOptionBreadcrumbPath = (option: Option, levels: string[]): string => {
  return levels.map((key) => getOptionLevelValue(option, key)).join(' › ');
};

/**
 * Sets indeterminate property on a checkbox element without direct param reassignment.
 * @param {HTMLInputElement | null} inputElement - The checkbox DOM element.
 * @param {boolean} isIndeterminate - Indeterminate state.
 */
const setCheckboxIndeterminate = (
  inputElement: HTMLInputElement | null,
  isIndeterminate: boolean,
) => {
  if (inputElement) {
    Object.assign(inputElement, { indeterminate: isIndeterminate });
  }
};

export interface MultiSelectDropdownProperties {
  name: string;
  options: Option[];
  selectedValues: string[];
  onSelectionChange: (name: string, values: string[]) => void;
  title?: string;
  placeholderFilled: string;
  placeholder?: string;
  icon?: React.JSX.Element;
  search?: boolean;
  multiSelectDisable?: boolean;
  fontSize?: 'small' | 'medium' | 'large';
  valueRequired?: boolean;
  error?: boolean;
  errorMessage?: string;
  version?: string;
  disabledAutoSorting?: boolean;
  selectedChips?: boolean;
  limitChips?: number;
  disabled?: boolean;
  tooltipText?: string;
  showTooltipForce?: boolean;
  drillDownLevels?: string[];
}

/**
 * MultiSelectDropdown component provides a dropdown menu allowing users to select multiple options.
 * Supports hierarchical key drill-down view with configurable level arrays (e.g. ['countryCode', 'partyId']).
 * @param {MultiSelectDropdownProperties} properties - Component properties.
 * @param {Option[]} properties.options - List of available options.
 * @param {string[]} properties.selectedValues - Array of selected option values.
 * @param {string} properties.name - Form field name identifier.
 * @param {(name: string, values: string[]) => void} properties.onSelectionChange - Callback triggered when selection changes.
 * @param {string} [properties.title] - Input label / title text.
 * @param {string} properties.placeholderFilled - Placeholder prefix when items are selected.
 * @param {string} [properties.placeholder] - Default placeholder text when nothing is selected.
 * @param {boolean} [properties.valueRequired] - Whether the input field is required.
 * @param {React.JSX.Element} [properties.icon] - Optional leading icon element.
 * @param {boolean} [properties.multiSelectDisable] - Disables checkboxes for single-select appearance.
 * @param {boolean} [properties.search] - Enables search input filter in dropdown.
 * @param {'small' | 'medium' | 'large'} [properties.fontSize] - Size variant.
 * @param {boolean} [properties.error] - Error state flag.
 * @param {string} [properties.errorMessage] - Error message text.
 * @param {string} [properties.version] - Layout version ('v1' | 'v2').
 * @param {boolean} [properties.disabledAutoSorting] - Disables alphabetical auto-sorting of options.
 * @param {boolean} [properties.selectedChips] - Renders selected values as removable chips.
 * @param {number} [properties.limitChips] - Maximum number of chips to display before summarizing.
 * @param {boolean} [properties.disabled] - Disabled state flag.
 * @param {string} [properties.tooltipText] - Tooltip translation key text.
 * @param {boolean} [properties.showTooltipForce] - Force displays tooltip.
 * @param {string[]} [properties.drillDownLevels] - Array of hierarchical drill-down keys (e.g. ['countryCode', 'partyId']).
 * @returns {React.JSX.Element} The rendered multi-select dropdown component.
 */
function MultiSelectDropdown({
  options,
  selectedValues,
  name,
  onSelectionChange,
  title = 'Select Role',
  placeholderFilled,
  placeholder = 'Select Option',
  valueRequired,
  icon,
  multiSelectDisable,
  search,
  fontSize,
  error,
  errorMessage,
  version,
  disabledAutoSorting = false,
  selectedChips,
  limitChips,
  disabled,
  tooltipText,
  showTooltipForce,
  drillDownLevels,
}: Readonly<MultiSelectDropdownProperties>): React.ReactElement {
  const reference = useRef<HTMLDivElement>(null);
  const { t } = useTranslation([
    'partnerFacilityManagement',
    'facilityManagement',
    'sharedComponent',
  ]);
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [, setSelectValue] = useState<string>('');
  const [drillDownPath, setDrillDownPath] = useState<string[]>([]);

  const hasDrillDown = Array.isArray(drillDownLevels) && drillDownLevels.length > 0;

  /**
   * Handles the change event for a single checkbox in the multi-select dropdown.
   * @param {string} value - The value of the checkbox that was toggled.
   */
  const handleCheckboxChange = (value: string) => {
    if (disabled) return;
    const newSelectedValues = selectedValues.includes(value)
      ? selectedValues.filter((option) => option !== value)
      : [...selectedValues, value];
    onSelectionChange(name, newSelectedValues);
    setSelectValue(newSelectedValues.toString());
  };

  /**
   * Handles toggling selection for all items in a drill-down group.
   * @param {DrillDownGroup} group - The drill-down group to toggle.
   */
  const handleGroupToggle = (group: DrillDownGroup) => {
    if (disabled) return;
    const groupItemValues = group.items.map((item) => item.value);
    let newSelectedValues: string[];
    if (group.isAllSelected) {
      newSelectedValues = selectedValues.filter(
        (existingValue) => !groupItemValues.includes(existingValue),
      );
    } else {
      const toAdd = groupItemValues.filter(
        (existingValue) => !selectedValues.includes(existingValue),
      );
      newSelectedValues = [...selectedValues, ...toAdd];
    }
    onSelectionChange(name, newSelectedValues);
    setSelectValue(newSelectedValues.toString());
  };

  /**
   * Handles toggling "Select All" for the current leaf items view.
   * @param {Option[]} items - The leaf options currently visible.
   */
  const handleSelectAllLeaf = (items: Option[]) => {
    if (disabled || items.length === 0) return;
    const itemValues = items.map((optionItem) => optionItem.value);
    const allSelected = items.every((optionItem) => selectedValues.includes(optionItem.value));
    let newSelectedValues: string[];
    if (allSelected) {
      newSelectedValues = selectedValues.filter(
        (existingValue) => !itemValues.includes(existingValue),
      );
    } else {
      const toAdd = itemValues.filter((existingValue) => !selectedValues.includes(existingValue));
      newSelectedValues = [...selectedValues, ...toAdd];
    }
    onSelectionChange(name, newSelectedValues);
    setSelectValue(newSelectedValues.toString());
  };

  /**
   * Navigates back one level in the drill-down hierarchy.
   */
  const handleDrillDownBack = () => {
    setDrillDownPath((previous) => previous.slice(0, -1));
  };

  /**
   * Navigates to a specific index in the drill-down path.
   * @param {number} targetIndex - Target index in the drill-down path (-1 for root).
   */
  const handleNavigateToBreadcrumb = (targetIndex: number) => {
    if (targetIndex < 0) {
      setDrillDownPath([]);
    } else {
      setDrillDownPath((previous) => previous.slice(0, targetIndex + 1));
    }
  };

  /**
   * Drills down into a specific group key.
   * @param {string} groupKey - Key value of the group to enter.
   */
  const handleDrillDownEnter = (groupKey: string) => {
    setDrillDownPath((previous) => [...previous, groupKey]);
  };

  // 1. Standard search filter for flat view
  const filteredOptions = options.filter((option) =>
    option.label?.toLowerCase().includes(searchTerm.trim().toLowerCase()),
  );

  // 2. Apply sorting only if auto-sorting is not disabled
  const sortedOptions = disabledAutoSorting
    ? filteredOptions
    : [...filteredOptions].sort((a, b) =>
        a.label.toLowerCase().localeCompare(b.label.toLowerCase(), undefined, { numeric: true }),
      );

  // 3. Drill-down calculations
  const currentLevelIndex = drillDownPath.length;
  const isAtLeafLevel = !hasDrillDown || currentLevelIndex >= (drillDownLevels?.length || 0);
  const currentLevelKey =
    hasDrillDown && !isAtLeafLevel && drillDownLevels ? drillDownLevels[currentLevelIndex] : null;

  const pathFilteredOptions =
    hasDrillDown && drillDownLevels
      ? options.filter((option) => {
          for (let index = 0; index < drillDownPath.length; index += 1) {
            const expectedValue = drillDownPath[index];
            const actualValue = getOptionLevelValue(option, drillDownLevels[index]);
            if (actualValue !== expectedValue) {
              return false;
            }
          }
          return true;
        })
      : options;

  const groupMap = new Map<string, Option[]>();
  if (hasDrillDown && currentLevelKey) {
    pathFilteredOptions.forEach((option) => {
      const groupValue = getOptionLevelValue(option, currentLevelKey);
      const existing = groupMap.get(groupValue);
      if (existing) {
        existing.push(option);
      } else {
        groupMap.set(groupValue, [option]);
      }
    });
  }

  const drillDownGroups: DrillDownGroup[] = Array.from(groupMap.entries()).map(([key, items]) => {
    const totalCount = items.length;
    const selectedCount = items.filter((optionItem) =>
      selectedValues.includes(optionItem.value),
    ).length;
    return {
      key,
      label: key,
      items,
      totalCount,
      selectedCount,
      isAllSelected: totalCount > 0 && selectedCount === totalCount,
      isPartialSelected: selectedCount > 0 && selectedCount < totalCount,
    };
  });

  if (!disabledAutoSorting) {
    drillDownGroups.sort((a, b) =>
      a.label.toLowerCase().localeCompare(b.label.toLowerCase(), undefined, { numeric: true }),
    );
  }

  const leafOptions = disabledAutoSorting
    ? pathFilteredOptions
    : [...pathFilteredOptions].sort((a, b) =>
        a.label.toLowerCase().localeCompare(b.label.toLowerCase(), undefined, { numeric: true }),
      );

  // Search matching options for drill-down mode
  const searchMatchingOptions = options.filter((option) => {
    const term = searchTerm.trim().toLowerCase();
    const matchesLabel = option.label?.toLowerCase().includes(term);
    const matchesValue = option.value?.toLowerCase().includes(term);
    const matchesDrillDown =
      hasDrillDown &&
      drillDownLevels &&
      drillDownLevels.some((key) => {
        const levelValue = getOptionLevelValue(option, key);
        return levelValue.toLowerCase().includes(term);
      });
    return matchesLabel || matchesValue || matchesDrillDown;
  });

  const handleClickOutside = useCallback((event: MouseEvent) => {
    if (reference.current && !reference.current.contains(event.target as Node)) {
      setIsOpen(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleClickOutside]);

  /**
   * Renders the drill-down dropdown content area.
   * @returns {React.JSX.Element} Drill-down list content.
   */
  const renderDrillDownContent = (): React.JSX.Element => {
    const isSearching = searchTerm.trim().length > 0;

    if (isSearching) {
      return (
        <div className="dropdown-lists">
          {searchMatchingOptions.length > 0 ? (
            searchMatchingOptions.map((option) => {
              const breadcrumbPath =
                hasDrillDown && drillDownLevels
                  ? getOptionBreadcrumbPath(option, drillDownLevels)
                  : '';
              return (
                <div
                  key={option.value}
                  className={`slide-left drilldown-search-item ${disabled ? 'disabled' : ''}`}>
                  <input
                    className={multiSelectDisable ? 'hide-checkbox' : ''}
                    type="checkbox"
                    checked={selectedValues.includes(option.value)}
                    onChange={() => handleCheckboxChange(option.value)}
                    disabled={disabled}
                  />
                  <div
                    className="drilldown-search-item-info"
                    role="presentation"
                    onClick={() => {
                      if (!disabled) handleCheckboxChange(option.value);
                    }}>
                    <div className="drilldown-search-item-label" title={option.label}>
                      {option.labelNode || option.label}
                    </div>
                    {breadcrumbPath && (
                      <span className="drilldown-search-item-path" title={breadcrumbPath}>
                        {breadcrumbPath}
                      </span>
                    )}
                  </div>
                </div>
              );
            })
          ) : (
            <p className="no-options">
              {t('noOptionsFound', { defaultValue: 'No options found' })}
            </p>
          )}
        </div>
      );
    }

    return (
      <>
        <div className="drilldown-header-bar">
          <div className="drilldown-breadcrumbs">
            <button
              type="button"
              className={`drilldown-breadcrumb-item ${drillDownPath.length === 0 ? 'active' : ''}`}
              onClick={() => handleNavigateToBreadcrumb(-1)}>
              {t('all', { defaultValue: 'All' })}
            </button>
            {drillDownPath.map((pathSegment, index) => (
              <React.Fragment key={`${pathSegment}-${index}`}>
                <span className="drilldown-breadcrumb-separator">›</span>
                <button
                  type="button"
                  className={`drilldown-breadcrumb-item ${
                    index === drillDownPath.length - 1 ? 'active' : ''
                  }`}
                  onClick={() => handleNavigateToBreadcrumb(index)}>
                  {pathSegment}
                </button>
              </React.Fragment>
            ))}
          </div>
          {drillDownPath.length > 0 && (
            <button
              type="button"
              className="drilldown-back-btn"
              onClick={handleDrillDownBack}
              title={t('back', { defaultValue: 'Back' })}>
              <RightArrowIcon className="drilldown-back-icon" />
              <span>{t('back', { defaultValue: 'Back' })}</span>
            </button>
          )}
        </div>

        <div className="drilldown-level-info">
          <span className="drilldown-level-title">
            {isAtLeafLevel
              ? t('selectOptions', { defaultValue: 'Options' })
              : `${t('by', { ns: ['partnerFacilityManagement', 'facilityManagement', 'sharedComponent'], defaultValue: 'By' })} ${t(currentLevelKey || '', { ns: ['partnerFacilityManagement', 'facilityManagement', 'sharedComponent'], defaultValue: formatLevelTitle(currentLevelKey || '') })}`}
          </span>
          {isAtLeafLevel && leafOptions.length > 0 && (
            <button
              type="button"
              className="drilldown-select-all-btn"
              onClick={() => handleSelectAllLeaf(leafOptions)}>
              {leafOptions.every((optionItem) => selectedValues.includes(optionItem.value))
                ? t('deselectAll', { defaultValue: 'Deselect All' })
                : t('selectAll', { defaultValue: 'Select All' })}
            </button>
          )}
        </div>

        <div className="dropdown-lists">
          {!isAtLeafLevel &&
            drillDownGroups.length > 0 &&
            drillDownGroups.map((group) => (
              <div key={group.key} className={`drilldown-group-row ${disabled ? 'disabled' : ''}`}>
                <input
                  type="checkbox"
                  checked={group.isAllSelected}
                  ref={(inputElement) =>
                    setCheckboxIndeterminate(inputElement, group.isPartialSelected)
                  }
                  onChange={(event) => {
                    event.stopPropagation();
                    handleGroupToggle(group);
                  }}
                  disabled={disabled}
                />
                <div
                  className="drilldown-group-content"
                  role="presentation"
                  onClick={() => handleDrillDownEnter(group.key)}>
                  <div className="drilldown-group-label-wrap">
                    <span className="drilldown-group-name" title={group.label}>
                      {t(group.label, { defaultValue: group.label })}
                    </span>
                    <span className="drilldown-group-count">
                      {group.selectedCount > 0
                        ? `${group.selectedCount}/${group.totalCount}`
                        : `${group.totalCount}`}
                    </span>
                  </div>
                  <button
                    type="button"
                    className="drilldown-enter-btn"
                    onClick={(event) => {
                      event.stopPropagation();
                      handleDrillDownEnter(group.key);
                    }}
                    aria-label={`Drill down into ${group.label}`}>
                    <RightArrowIcon className="drilldown-enter-icon" />
                  </button>
                </div>
              </div>
            ))}

          {!isAtLeafLevel && drillDownGroups.length === 0 && (
            <p className="no-options">
              {t('noOptionsFound', { defaultValue: 'No options found' })}
            </p>
          )}

          {isAtLeafLevel &&
            leafOptions.length > 0 &&
            leafOptions.map((option) => (
              <span key={option.value} className={`slide-left ${disabled ? 'disabled' : ''}`}>
                <input
                  className={multiSelectDisable ? 'hide-checkbox' : ''}
                  type="checkbox"
                  checked={selectedValues.includes(option.value)}
                  onChange={() => handleCheckboxChange(option.value)}
                  disabled={disabled}
                />
                <p
                  role="presentation"
                  onClick={() => {
                    if (!disabled) handleCheckboxChange(option.value);
                  }}
                  title={option.label}>
                  {option.labelNode ||
                    (option.label && option.label.length > 20
                      ? `${option.label.substring(0, 20)}...`
                      : option.label)}
                </p>
              </span>
            ))}

          {isAtLeafLevel && leafOptions.length === 0 && (
            <p className="no-options">
              {t('noOptionsFound', { defaultValue: 'No options found' })}
            </p>
          )}
        </div>
      </>
    );
  };

  /**
   * Renders standard flat options dropdown content.
   * @returns {React.JSX.Element} Flat options list.
   */
  const renderFlatContent = (): React.JSX.Element => {
    return (
      <div className="dropdown-lists">
        {sortedOptions.length > 0 ? (
          sortedOptions.map((option) => (
            <span key={option.value} className={`slide-left ${disabled ? 'disabled' : ''}`}>
              <input
                className={multiSelectDisable ? 'hide-checkbox' : ''}
                type="checkbox"
                checked={selectedValues.includes(option.value)}
                onChange={() => handleCheckboxChange(option.value)}
                disabled={disabled}
              />
              <p
                role="presentation"
                onClick={() => {
                  if (!disabled) handleCheckboxChange(option.value);
                }}
                title={option.label}>
                {option.labelNode ||
                  (option.label && option.label.length > 20
                    ? `${option.label.substring(0, 20)}...`
                    : option.label)}
              </p>
            </span>
          ))
        ) : (
          <p className="no-options">{t('noOptionsFound', { defaultValue: 'No options found' })}</p>
        )}
      </div>
    );
  };

  return (
    <>
      {version === 'v1' && (
        <div className={`multi-select-dropdown-v1 ${fontSize}`}>
          <div className="multi-select-header">
            {title && (
              <p className={`input-label ${error ? 'error' : ''}`}>
                {title}
                {valueRequired && <span>*</span>}
                {tooltipText && (
                  <span
                    className={`info-tooltip-container ${showTooltipForce ? 'show-tooltip' : ''}`}>
                    <InfoIcon />
                    <span className="info-tooltip-text">{t(tooltipText)}</span>
                  </span>
                )}
              </p>
            )}
          </div>
          <div className={`multi-select-dropdown-list ${isOpen ? 'open' : ''}`} ref={reference}>
            <div
              className={`dropdown-header ${error ? 'error' : ''}`}
              onClick={() => setIsOpen((previous) => !previous)}
              role="presentation">
              <div className="left-section">
                {icon}
                <div className="selected-chips">
                  {selectedChips && selectedValues.length > 0 ? (
                    <>
                      {selectedValues
                        .slice(0, limitChips && limitChips > 0 ? limitChips : selectedValues.length)
                        .map((value) => {
                          const selectedItem = options.find(
                            (optionItem) => optionItem.value === value,
                          );
                          return (
                            <span className="chip" key={value} title={selectedItem?.label}>
                              {selectedItem?.label && selectedItem.label.length > 20
                                ? `${selectedItem.label.substring(0, 20)}...`
                                : selectedItem?.label}
                              <button
                                type="button"
                                className="remove-chip"
                                onClick={(even) => {
                                  even.stopPropagation();
                                  handleCheckboxChange(value);
                                }}
                                aria-label={`Remove ${selectedItem?.label}`}>
                                ✖
                              </button>
                            </span>
                          );
                        })}
                      {limitChips && limitChips > 0 && selectedValues.length > limitChips && (
                        <span className="chip-count">
                          +{selectedValues.length - limitChips} more
                        </span>
                      )}
                    </>
                  ) : (
                    <span>
                      {selectedValues.length
                        ? `${placeholderFilled} ${t('selected', { defaultValue: 'selected' })}`
                        : placeholder}
                    </span>
                  )}
                </div>{' '}
              </div>
              <span className="dropdown-arrow">{isOpen ? <DownArrowIcon /> : <ArrowIcon />}</span>
            </div>
            {isOpen && (
              <div
                className={`dropdown-content ${isOpen ? 'open' : ''} ${
                  search ? 'search-border-none' : ''
                }`}>
                {search && (
                  <div className="multi-select-search">
                    <input
                      type="text"
                      className="dropdown-search"
                      placeholder={t('search', { defaultValue: 'Search...' })}
                      value={searchTerm}
                      onChange={(event) => setSearchTerm(event.target.value)}
                    />
                    <div className="search-box">
                      <span>
                        <Search />
                      </span>
                    </div>
                  </div>
                )}
                <div className={isOpen ? 'dropdown-list' : 'opened'}>
                  {hasDrillDown ? renderDrillDownContent() : renderFlatContent()}
                </div>
              </div>
            )}
          </div>
          {error && <p className="custom-input-error-message">{errorMessage}</p>}
        </div>
      )}
      {version === 'v2' && (
        <div className={`multi-select-dropdown-v2 ${fontSize}`}>
          <div className="multi-select-header">
            {title && (
              <p className={`input-label ${error ? 'error' : ''}`}>
                {title}
                {icon}
                {valueRequired && <span>*</span>}
                {tooltipText && (
                  <span
                    className={`info-tooltip-container ${showTooltipForce ? 'show-tooltip' : ''}`}>
                    <InfoIcon />
                    <span className="info-tooltip-text">{t(tooltipText)}</span>
                  </span>
                )}
              </p>
            )}
          </div>
          <div className="multi-select-dropdown-list">
            <div
              className={`dropdown-content ${search ? 'search-border-none' : ''}`}
              ref={reference}>
              {search && (
                <div className="multi-select-search">
                  <input
                    type="text"
                    className="dropdown-search"
                    placeholder={t('search', { defaultValue: 'Search...' })}
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                  />
                  <div className="search-box">
                    <span>
                      <SearchIcon />
                    </span>
                  </div>
                </div>
              )}
              <div className="dropdown-list">
                {hasDrillDown ? renderDrillDownContent() : renderFlatContent()}
              </div>
            </div>
          </div>
          {error && <p className="custom-input-error-message">{errorMessage}</p>}
        </div>
      )}
    </>
  );
}

MultiSelectDropdown.defaultProps = {
  title: '',
  placeholder: '',
  valueRequired: false,
  icon: null,
  search: false,
  multiSelectDisable: false,
  fontSize: 'small',
  error: false,
  errorMessage: '',
  version: 'v1',
  disabledAutoSorting: false,
  selectedChips: false,
  limitChips: null,
  disabled: false,
  tooltipText: '',
  showTooltipForce: false,
  drillDownLevels: undefined,
};

export default MultiSelectDropdown;
